import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { useMainStateStore } from "@/components/main/stores/mainStateStore";
import { useCreateModel } from "@/components/main/utils/models/useCreateModel";
import { VersionChip } from "@/components/main/utils/common/VersionChip";
import {
  ColumnBottomSpacer,
  ColumnTopSpacer,
  OVERVIEW_LIST_ROWS,
} from "@/components/main/pages/pageColumn";
import {
  useDuplicateSessionNotice,
  useModelsPolling,
} from "@/components/main/hooks/useModelsPolling";
import { HeroSearch } from "@/components/main/overview/HeroSearch";
import { SpaceFilterRule } from "@/components/main/overview/SpaceFilterRule";
import { OrgInviteBanner } from "@/components/main/organizations/OrgInviteBanner";
import { SectionRule } from "components/common/SectionRule";
import { ModelRows } from "@/components/main/overview/ModelRows";
import { ModelsLoading } from "@/components/main/overview/ModelsLoading";
import { PinnedColumn } from "@/components/main/overview/PinnedColumn";
import { CreateModelStrip } from "@/components/main/overview/CreateModelStrip";
import { NewModelOverlay } from "@/components/main/overview/NewModelOverlay";
import {
  selectAll,
  selectPinned,
  selectRecentForDisplay,
  hasLiveModels,
} from "@/components/main/overview/selectors";
import { useCommandK } from "@/components/main/overview/useCommandK";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";

export const OverviewPage: React.FC = () => {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [showAll, setShowAll] = useState(false);
  const { isOnline, isAppInitialized, modelsInitialized } = useModelsPolling();
  useDuplicateSessionNotice();
  useCommandK(searchInputRef);
  const { freshModelId, createModel } = useCreateModel();
  const paneRef = useRef<HTMLDivElement>(null);
  const listsRef = useRef<HTMLDivElement>(null);
  // Open with the space the card names; `null` is "ask", never a default.
  const [naming, setNaming] = useState<{ spaceId: string | null } | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const newModelIn = (location.state as { newModelIn?: string } | null)
    ?.newModelIn;
  // The org-created page hands its starter space over; consumed once, so a
  // refresh or Back does not reopen the card.
  useEffect(() => {
    if (!newModelIn) return;
    setNaming({ spaceId: newModelIn });
    void navigate(location.pathname, { replace: true, state: null });
  }, [newModelIn, location.pathname, navigate]);

  const availableModels = useMainStateStore((state) => state.availableModels);
  const selectedSpaceId = useOrgContextStore((s) => s.selectedSpaceId);
  const setSelectedSpace = useOrgContextStore((s) => s.setSelectedSpace);
  // Catalog models belong to no org and have their own page.
  const models = useMemo(
    () => availableModels.filter((m) => !m.is_catalog),
    [availableModels],
  );
  // The rail's space filter narrows every bucket on the page.
  const scoped = selectedSpaceId
    ? models.filter((m) => m.space_id === selectedSpaceId)
    : models;

  const pinned = selectPinned(scoped);
  // Falls back to all models when nothing is recent or pinned yet, so a first
  // login with only shared models still surfaces them here (not an empty hint).
  const recent = selectRecentForDisplay(scoped);
  const all = selectAll(scoped);

  const visibleRows = (showAll ? all.length : recent.length) || 1;
  const listHeight = `${Math.min(visibleRows, OVERVIEW_LIST_ROWS) * 2}rem`;
  // Below the cap the list height matches its content exactly. Keeping overflow
  // hidden below the cap stops a scrollbar flashing while the height transition
  // catches up to a new row.
  const listScrolls = visibleRows > OVERVIEW_LIST_ROWS;

  if (isAppInitialized && !modelsInitialized) {
    return <ModelsLoading isOnline={isOnline} label="Loading models..." />;
  }

  if (!isAppInitialized) {
    return null;
  }

  // Zero models renders the same shell as the populated overview — the Pinned
  // and Recent sections carry their own "…will appear here" hints and the
  // create strip pulses its New model button, so the layout never jumps once the
  // first model lands.
  return (
    <div
      ref={paneRef}
      className="relative flex flex-col items-center w-full h-full overflow-y-auto px-6"
      style={{
        fontSize: "var(--font-size)",
        opacity: isOnline ? 1 : 0.55,
        transition: "opacity var(--duration-short) var(--ease-move)",
      }}
    >
      <ColumnTopSpacer />
      <div className="w-full" style={{ maxWidth: "45rem" }}>
        <HeroSearch inputRef={searchInputRef} />
        <OrgInviteBanner />
        {/* 32 above the head, 16 below: it names the lists, so it sits nearer
            them than to the search. */}
        <div className="mt-8">
          <SpaceFilterRule
            spaceId={selectedSpaceId}
            onSelect={setSelectedSpace}
          />
        </div>
        <div
          ref={listsRef}
          data-naming-anchor
          className="overview-cols mt-4"
          style={{
            gridTemplateColumns: showAll ? "0fr 1fr" : "1fr 1fr",
            columnGap: showAll ? "0px" : "var(--spacing-3xl)",
          }}
        >
          <div
            className="overview-pinned"
            style={{ opacity: showAll ? 0 : 1 }}
            aria-hidden={showAll}
          >
            <PinnedColumn models={pinned} />
          </div>
          <div className="min-w-0">
            <SectionRule
              inset
              icon={
                showAll ? (
                  <DashboardOutlinedIcon sx={{ fontSize: 14 }} />
                ) : (
                  <AccessTimeIcon sx={{ fontSize: 14 }} />
                )
              }
              label={showAll ? `All models · ${all.length}` : "Recent"}
              action={{
                label: showAll ? "Show recent" : "Show all",
                onClick: () => setShowAll((v) => !v),
                testId: "toggle-all-models",
              }}
            />
            <div
              className={`overview-list ${
                listScrolls ? "overflow-y-auto" : "overflow-y-hidden"
              } ${showAll && listScrolls ? "pr-1" : ""}`}
              style={{ height: listHeight }}
              data-testid={showAll ? "all-models-list" : undefined}
            >
              {showAll ? (
                <ModelRows
                  models={all}
                  freshModelId={freshModelId}
                  showActiveCount
                  movable
                />
              ) : recent.length > 0 ? (
                <ModelRows
                  models={recent}
                  freshModelId={freshModelId}
                  movable
                />
              ) : (
                <div className="flex items-center gap-2 item-height px-1 text-small text-fg-muted italic">
                  <span className="w-5 shrink-0" />
                  Open a model and it&apos;ll show up here.
                </div>
              )}
            </div>
          </div>
        </div>
        <CreateModelStrip
          hasModels={hasLiveModels(models)}
          isNaming={!!naming}
          onNewModel={() => setNaming({ spaceId: selectedSpaceId })}
        />
        <NewModelOverlay
          open={!!naming}
          target={naming ?? { spaceId: null }}
          anchorRef={listsRef}
          paneRef={paneRef}
          createModel={createModel}
          onClose={() => setNaming(null)}
        />
      </div>
      <ColumnBottomSpacer />
      <div className="absolute bottom-4 right-4 z-10">
        <VersionChip />
      </div>
    </div>
  );
};
